<template>
    <div>
        <h2 class="font-semibold text-2xl leading-tight">Getting paid</h2>
        <p class="mt-1 text-sm text-gray-600">Where rent from tenants and partner earnings are deposited.</p>

        <div class="mt-4 space-y-6">
        <div class="space-y-4">
        <!-- Locked: rent payments are a Pro feature -->
        <div v-if="!hasRentPaymentsSubscription">
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div class="flex items-center">
                    <div class="flex-shrink-0">
                        <font-awesome-icon :icon="faInfoCircle" class="text-blue-400" />
                    </div>
                    <div class="ml-3">
                        <h3 class="text-sm font-medium text-blue-800">Rent payments are a Pro feature</h3>
                        <div class="mt-2 text-sm text-blue-700">
                            <p>Rent payments and maintenance are included with RiskGuard Pro.</p>
                            <p v-if="proCtaState" class="mt-2">
                                <button type="button" class="text-blue-600 hover:text-blue-800 underline" @click="$emit('start-pro-upgrade')">
                                    {{ lockedCtaLabel }}
                                </button>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Rent Payments Setup -->
        <div v-if="hasRentPaymentsAccess" id="rent-payments-setup">
            <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg p-6">
                <div class="flex justify-between items-center mb-3">
                    <h3 class="text-lg font-semibold text-gray-900">Rent payments</h3>
                    <div v-if="rentSetupIsComplete && bankAccounts && bankAccounts.length" class="flex items-center space-x-4">
                        <button class="primaryButton" @click="$emit('create-link-token', 'auth')">Add Bank Account</button>
                    </div>
                </div>
                <div v-if="rentSetupShowStepper" class="mb-6">
                    <div v-if="rentSetupIsComplete" class="mb-6 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
                        <p class="text-sm font-medium text-green-800">
                            Rent payments are set up. You can now receive rent from tenants.
                        </p>
                    </div>

                    <div v-if="!rentSetupIsComplete" class="mb-6">
                        <p class="text-sm text-gray-600">Complete these 3 steps to start receiving rent from tenants.</p>
                        <div class="mt-3 flex items-center justify-between gap-4">
                            <p class="text-sm font-semibold text-gray-900">
                                {{ rentSetupCompletedCount }} of 3 steps complete
                                <span v-if="rentSetupCurrentStep" class="font-normal text-gray-500">
                                    · You're on Step {{ rentSetupCurrentStep.stepNumber }}
                                </span>
                            </p>
                            <span class="text-sm font-medium text-gray-600">{{ rentSetupProgressPercent }}%</span>
                        </div>
                        <div class="mt-2 h-2 w-full rounded-full bg-gray-200">
                            <div class="h-2 rounded-full bg-gold-900 transition-all duration-300" :style="{ width: `${rentSetupProgressPercent}%` }"></div>
                        </div>
                    </div>

                    <nav aria-label="Rent payments setup progress">
                        <ol role="list" class="overflow-hidden">
                            <li
                                v-for="(step, index) in rentSetupDisplaySteps"
                                :key="step.key"
                                :class="[index !== rentSetupDisplaySteps.length - 1 ? 'pb-8' : '', 'relative']"
                            >
                                <div
                                    class="relative flex w-full items-start gap-4 rounded-lg border p-4"
                                    :class="{
                                        'border-green-200 bg-green-50/40': step.uiStatus === 'completed',
                                        'border-red-200 bg-red-50/40 shadow-sm': step.isDenied,
                                        'border-rent-risk-blue-400 bg-rent-risk-blue-100 shadow-sm': step.uiStatus === 'in-progress' && !step.isDenied,
                                        'border-gray-200 bg-gray-50/60': step.uiStatus === 'remaining',
                                    }"
                                >
                                    <span class="flex h-9 shrink-0 items-center">
                                        <span v-if="step.uiStatus === 'completed'" class="relative z-10 flex size-8 items-center justify-center rounded-full bg-green-600">
                                            <font-awesome-icon :icon="faCheck" class="text-sm text-white" aria-hidden="true" />
                                        </span>
                                        <span v-else-if="step.isDenied" class="relative z-10 flex size-8 items-center justify-center rounded-full border-2 border-red-600 bg-white" aria-current="step">
                                            <span class="text-sm font-semibold text-red-600">{{ step.stepNumber }}</span>
                                        </span>
                                        <span v-else-if="step.uiStatus === 'in-progress'" class="relative z-10 flex size-8 items-center justify-center rounded-full border-2 border-rent-risk-blue-400 bg-white" aria-current="step">
                                            <span class="text-sm font-semibold text-rent-risk-blue-400">{{ step.stepNumber }}</span>
                                        </span>
                                        <span v-else class="relative z-10 flex size-8 items-center justify-center rounded-full border-2 border-gray-300 bg-white">
                                            <span class="text-sm font-medium text-gray-400">{{ step.stepNumber }}</span>
                                        </span>
                                    </span>

                                    <div class="min-w-0 flex-1">
                                        <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
                                            <h4
                                                class="text-base font-semibold"
                                                :class="{
                                                    'text-green-700': step.uiStatus === 'completed',
                                                    'text-red-700': step.isDenied,
                                                    'text-rent-risk-blue-400': step.uiStatus === 'in-progress' && !step.isDenied,
                                                    'text-gray-500': step.uiStatus === 'remaining',
                                                }"
                                            >
                                                Step {{ step.stepNumber }} · {{ step.name }}
                                            </h4>
                                            <span class="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset" :class="step.badge.class">
                                                {{ step.badge.label }}
                                            </span>
                                        </div>

                                        <p v-if="step.uiStatus === 'completed' && step.summary" class="mt-1 text-sm text-gray-600">{{ step.summary }}</p>
                                        <p v-if="step.uiStatus === 'remaining' && step.lockedMessage" class="mt-1 text-sm text-gray-500">{{ step.lockedMessage }}</p>

                                        <div v-if="step.uiStatus === 'in-progress'" class="mt-3 space-y-3">
                                            <template v-if="step.key === 'kyc'">
                                                <p class="text-sm text-gray-600">
                                                    We use Plaid to securely verify your identity. Your information is encrypted and protected.
                                                </p>
                                                <button class="primaryButton" @click="$emit('create-link-token', 'identity_verification')">
                                                    Start Identity Verification
                                                </button>
                                            </template>

                                            <template v-else-if="step.key === 'bank-account'">
                                                <p class="text-sm text-gray-600">
                                                    Link the bank account where you want to receive rent payments. Transactions are processed securely through Plaid.
                                                </p>
                                                <button class="primaryButton" @click="$emit('create-link-token', 'auth')">Add Bank Account</button>
                                            </template>

                                            <template v-else-if="step.key === 'onboarding'">
                                                <p v-if="isOnboardingDenied" class="text-sm text-red-700">
                                                    Your Plaid onboarding application was denied.
                                                </p>
                                                <p
                                                    v-else-if="!isOnboardingInfoSubmitted && !needsPlaidOwnerVerification && (!transferDiligenceStatus || transferDiligenceStatus === 'approved')"
                                                    class="text-sm text-gray-600"
                                                >
                                                    Provide your business verification details through Plaid so we can enable rent transfers.
                                                </p>
                                                <alert-card v-else-if="transferDiligenceStatus === 'pending' || transferDiligenceStatus === 'under_review'" :type="'info'">
                                                    <p>
                                                        Your onboarding approval is in progress. Once approved, you will be able to access rent payment features. Please visit this page to check your status.
                                                    </p>
                                                </alert-card>
                                                <ul v-else-if="transferDiligenceStatus === 'more_information_required'" class="space-y-2">
                                                    <li
                                                        v-for="(requirement, requirementIndex) in originator?.outstanding_requirements || []"
                                                        :key="requirementIndex"
                                                        class="text-sm font-medium text-amber-800"
                                                    >
                                                        {{ formatString(requirement.requirement_type) }}
                                                    </li>
                                                </ul>

                                                <template v-if="!isOnboardingDenied">
                                                    <inertia-link
                                                        v-if="needsPlaidOwnerVerification && bankAccounts && bankAccounts.length"
                                                        :href="route('plaid.owner-verification')"
                                                        class="primaryButton inline-flex"
                                                    >
                                                        Complete Owner Verification
                                                    </inertia-link>
                                                    <button
                                                        v-else-if="!isOnboardingInfoSubmitted && bankAccounts && bankAccounts.length"
                                                        class="primaryButton"
                                                        @click="$emit('onboarding-step1')"
                                                    >
                                                        Complete Business Verification
                                                    </button>
                                                    <button
                                                        v-else-if="!needsPlaidOwnerVerification && transferDiligenceStatus === 'more_information_required' && originator?.outstanding_requirements?.length"
                                                        class="primaryButton"
                                                        @click="$emit('show-missing-req')"
                                                    >
                                                        Update Information
                                                    </button>
                                                </template>

                                                <button
                                                    v-if="plaidResubmissionEnabled"
                                                    type="button"
                                                    class="primaryButton mt-3"
                                                    @click="$emit('show-plaid-resubmission')"
                                                >
                                                    Submit Required Information
                                                </button>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </ol>
                    </nav>
                </div>

                <!-- Bank Accounts Table -->
                <div v-if="bankAccounts && bankAccounts.length" class="mt-6">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden border rounded-lg">
                                <table class="min-w-full divide-y divide-gray-300 hidden lg:table">
                                    <thead class="bg-blue-50">
                                        <tr>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">Account Last 4 Digits</th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">Bank Name</th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">Account Title</th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(account, index) in bankAccounts" :key="account.id"
                                            :class="[(index % 2 === 0 ? 'bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer' : 'bg-white border-b hover:bg-yellow-50 hover:cursor-pointer')]">
                                            <td class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                <div class="flex">
                                                    {{ account.account_last_4 }}
                                                    <span v-if="account.is_funding_account"
                                                          class="inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset bg-green-50 text-green-700 ring-green-600/20 ml-2">
                                                        Funding Account
                                                    </span>
                                                </div>
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">{{ account.bank_name }}</td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">{{ account.account_name }}</td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900"></td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div class="lg:hidden p-4 space-y-4">
                                    <div v-for="account in bankAccounts" :key="account.id" class="bg-white p-4 rounded-lg shadow-md border">
                                        <h4 class="text-sm font-medium text-gray-900">
                                            **** **** **** {{ account.account_last_4 }}
                                            <span v-if="account.is_funding_account"
                                                  class="inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset bg-green-50 text-green-700 ring-green-600/20 ml-2">
                                                Funding Account
                                            </span>
                                        </h4>
                                        <p class="mt-1 text-sm text-gray-500"><span class="text-gray-900">Bank Name:</span> {{ account.bank_name }}</p>
                                        <p class="mt-1 text-sm text-gray-500"><span class="text-gray-900">Account Title:</span> {{ account.account_name }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Phone Number Required -->
        <div v-if="hasRentPaymentsSubscription && needsPhoneNumber">
            <div class="bg-orange-50 border border-orange-200 rounded-lg p-6">
                <div class="flex items-center">
                    <div class="flex-shrink-0">
                        <font-awesome-icon :icon="faPhone" class="text-orange-400" />
                    </div>
                    <div class="ml-3">
                        <h3 class="text-sm font-medium text-orange-800">Phone Number Required</h3>
                        <div class="mt-2 text-sm text-orange-700">
                            <p>To complete the setup for rent payments, we need to verify your phone number. This helps ensure secure transactions and account verification.</p>
                            <p class="mt-2">
                                <button class="text-orange-600 hover:text-orange-800 underline" @click="$emit('show-phone')">Add Phone Number</button>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
</div>
        <!-- Partner Program Payouts -->
        <div>
            <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg p-6">
                <div class="flex justify-between items-center mb-3">
                    <h3 class="text-lg font-semibold text-gray-900">Partner payouts</h3>
                    <a v-if="affiliateAccount" :href="route('partners.bank_account.refresh')" class="primaryButton">Update Bank Account</a>
                    <a v-else :href="route('partners.create')" class="primaryButton">Add Bank Account</a>
                </div>
                <div class="text-sm mb-3">For Partner Program Payouts</div>
                <div class="overflow-x-auto">
                    <div class="align-middle inline-block min-w-full">
                        <div class="shadow overflow-hidden border rounded-lg">
                            <table class="min-w-full divide-y divide-gray-300 hidden lg:table">
                                <thead class="bg-blue-50">
                                    <tr>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">Account Last 4 Digits</th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">Bank Name</th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">Missing Information</th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="affiliateAccount" class="bg-gray-50 border-b hover:bg-yellow-50">
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ affiliateAccount?.external_accounts?.data[0]?.last4 ?? 'N/A' }}</td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ affiliateAccount?.external_accounts?.data[0]?.bank_name }}</td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <ul>
                                                <li v-for="(requirement, i) in missingInformation(affiliateAccount)" :key="i" v-html="requirement"></li>
                                            </ul>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                                            <span class="inline-flex leading-5 font-semibold rounded-full mt-2">
                                                <span v-if="!affiliateAccount.requirements.eventually_due.length && !affiliateAccount.requirements.currently_due.length"
                                                      class="rounded-md py-1 px-2 text-xs font-medium ring-1 ring-inset text-green-700 bg-green-50 ring-green-600/20">Completed</span>
                                                <span v-else class="rounded-md py-1 px-2 text-xs font-medium ring-1 ring-inset text-red-700 bg-red-50 ring-red-600/10">Incomplete</span>
                                            </span>
                                        </td>
                                    </tr>
                                    <tr v-show="!affiliateAccount" class="hover:bg-yellow-50">
                                        <td colspan="4" class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">No bank account found.</td>
                                    </tr>
                                </tbody>
                            </table>

                            <div class="space-y-6 lg:hidden">
                                <div v-if="affiliateAccount" class="bg-white p-4 rounded-lg shadow-md">
                                    <div class="mb-2 text-sm text-gray-500"><span class="font-bold">Account Last 4 Digits: </span>{{ affiliateAccount?.external_accounts?.data[0]?.last4 ?? 'N/A' }}</div>
                                    <div class="mb-2 text-sm text-gray-500"><span class="font-bold">Bank Name: </span>{{ affiliateAccount?.external_accounts?.data[0]?.bank_name }}</div>
                                    <div class="mb-2 text-sm text-gray-500">
                                        <span class="font-bold">Missing Information: </span>
                                        <ul class="list-disc pl-5">
                                            <li v-for="(requirement, i) in missingInformation(affiliateAccount)" :key="i" v-html="requirement"></li>
                                        </ul>
                                    </div>
                                    <div class="mb-2 text-sm text-gray-500">
                                        <span class="font-bold">Status: </span>
                                        <span v-if="!affiliateAccount.requirements.eventually_due.length && !affiliateAccount.requirements.currently_due.length"
                                              class="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium bg-green-100 text-green-700">Completed</span>
                                        <span v-else class="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium bg-red-100 text-red-700">Incomplete</span>
                                    </div>
                                </div>
                                <div v-show="!affiliateAccount" class="bg-white p-4 rounded-lg shadow-md text-center text-gray-400 text-sm">
                                    <span class="font-bold">No bank account found.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </div>
    </div>
</template>

<script>
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { faInfoCircle, faPhone } from '@fortawesome/pro-light-svg-icons';
import AlertCard from '@/Shared/AlertCard.vue';

export default {
    name: 'GettingPaidSection',
    components: {
        AlertCard,
    },
    props: {
        affiliateAccount: { type: Object, default: null },
        bankAccounts: { type: Array, default: () => [] },
        isKyc: { type: Boolean, default: false },
        transferDiligenceStatus: { type: String, default: null },
        isOnboardingInfoSubmitted: { type: Boolean, default: false },
        needsPlaidOwnerVerification: { type: Boolean, default: false },
        originator: { type: Object, default: null },
        steps: { type: Array, default: () => [] },
        plaidResubmissionEnabled: { type: Boolean, default: false },
        needsPhoneNumber: { type: Boolean, default: false },
        hasRentPaymentsAccess: { type: Boolean, default: false },
        hasRentPaymentsSubscription: { type: Boolean, default: false },
        proCtaState: { type: String, default: null },
        isOnEarlierPlan: { type: Boolean, default: false },
    },
    emits: [
        'create-link-token',
        'onboarding-step1',
        'show-missing-req',
        'show-plaid-resubmission',
        'show-phone',
        'start-pro-upgrade',
    ],
    data() {
        return {
            faCheck,
            faInfoCircle,
            faPhone,
        };
    },
    computed: {
        lockedCtaLabel() {
            if (this.proCtaState === 'fix-card') {
                return 'Update your card';
            }

            return this.isOnEarlierPlan ? 'Switch to RiskGuard Pro' : 'Subscribe to RiskGuard Pro';
        },
        rentSetupShowStepper() {
            return Array.isArray(this.steps) && this.steps.length > 0;
        },
        rentSetupIsComplete() {
            return this.transferDiligenceStatus === 'approved';
        },
        isOnboardingDenied() {
            return ['denied', 'rejected'].includes(this.transferDiligenceStatus);
        },
        rentSetupDisplaySteps() {
            if (!this.rentSetupShowStepper) {
                return [];
            }

            return this.steps.map((step, index) => {
                const display = {
                    ...step,
                    stepNumber: index + 1,
                    uiStatus: step.status,
                    isDenied: step.key === 'onboarding' && this.isOnboardingDenied,
                };

                display.badge = this.getRentSetupStepBadge(display);
                display.summary = this.getRentSetupStepSummary(step);
                display.lockedMessage = display.uiStatus === 'remaining' ? `Complete Step ${index} first` : null;

                return display;
            });
        },
        rentSetupCompletedCount() {
            return this.rentSetupDisplaySteps.filter((step) => step.uiStatus === 'completed').length;
        },
        rentSetupProgressPercent() {
            return Math.round((this.rentSetupCompletedCount / 3) * 100);
        },
        rentSetupCurrentStep() {
            return this.rentSetupDisplaySteps.find((step) => step.uiStatus === 'in-progress') || null;
        },
    },
    methods: {
        formatString(input) {
            return input.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
        },
        missingInformation(account) {
            const combinedArray = [...account.requirements.currently_due, ...account.requirements.eventually_due];
            const uniqueArray = [...new Set(combinedArray)];
            let hasDob = false;

            return uniqueArray.map((item) => {
                if (item.includes('dob') && !hasDob) {
                    hasDob = true;
                    return 'Date of Birth <small>(Resume Setup > Personal Information > Edit)</small>';
                } else if (item.includes('ssn_last_4')) {
                    return 'SSN Last 4 <small>(Resume Setup > Personal Information > Edit)</small>';
                } else if (item.includes('verification') && item.includes('document')) {
                    return 'Verification Document <small>(Resume Setup > Personal Information > Edit)</small>';
                } else if (!item.includes('dob')) {
                    const value = item
                        .replace(/[_.]/g, ' ')
                        .split(' ')
                        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');

                    return `${value} <small>(Resume Setup)`;
                }

                return undefined;
            });
        },
        getRentSetupStepBadge(step) {
            if (step.uiStatus === 'completed') {
                return { label: 'Completed', class: 'bg-green-50 text-green-700 ring-green-600/20' };
            }

            if (step.uiStatus === 'remaining') {
                return { label: 'Up next', class: 'bg-gray-50 text-gray-600 ring-gray-500/20' };
            }

            if (step.key === 'onboarding') {
                if (this.isOnboardingDenied) {
                    return { label: 'Denied', class: 'bg-red-50 text-red-700 ring-red-600/20' };
                }

                if (this.needsPlaidOwnerVerification) {
                    return { label: 'Action required', class: 'bg-amber-50 text-amber-800 ring-amber-600/20' };
                }

                if (['pending', 'under_review'].includes(this.transferDiligenceStatus)) {
                    return { label: 'Under review', class: 'bg-blue-50 text-blue-700 ring-blue-600/20' };
                }

                if (this.transferDiligenceStatus === 'more_information_required') {
                    return { label: 'Action required', class: 'bg-amber-50 text-amber-800 ring-amber-600/20' };
                }
            }

            return { label: 'Current step', class: 'bg-rent-risk-blue-100 text-rent-risk-blue-400 ring-rent-risk-blue-400/20' };
        },
        getRentSetupStepSummary(step) {
            if (step.key === 'kyc' && this.isKyc) {
                return 'Identity verified';
            }

            if (step.key === 'bank-account' && this.bankAccounts?.length) {
                const account = this.bankAccounts[0];
                return `${account.bank_name} ····${account.account_last_4}`;
            }

            if (step.key === 'onboarding' && this.transferDiligenceStatus === 'approved') {
                return 'Verification approved';
            }

            return null;
        },
    },
};
</script>
